// Annotation store (Unit 17, Component 16).
//
// Holds the active paper's annotation list, the selected card (if any), and
// the accept/dismiss/edit actions. Server is the source of truth — each
// action POSTs to /api/annotations/{id} and then revalidates the SWR cache
// keyed by /api/papers/{id}/annotations so the canvas overlay and the cards
// list stay in lockstep.

import { mutate } from "swr";
import { create } from "zustand";

import { ApiError, apiFetch, apiJson } from "../api/client";
import type {
  Annotation,
  AnnotationStatus,
} from "../api/types";

interface AnnotationActionPayload {
  status?: AnnotationStatus;
  accepted_text?: string;
  point_deduction?: number;
  content_note?: string;
}

interface AnnotationStoreState {
  annotations: Annotation[];
  selectedId: number | null;
  /** Identifies the word currently shown with a selection frame on the canvas.
   * Format: "${pageNumber}|${x_norm.toFixed(3)}|${y_norm.toFixed(3)}" */
  selectedWordKey: string | null;
  /** Annotations highlighted as a group in the scan — set when a raster
   * criterion/task is expanded, so all its contributing flags light up at once
   * (regardless of the card-type visibility filter). */
  highlightIds: Set<number>;
  /** A green frame drawn on the scan for a clicked content task — the answer
   * region the task is graded against. Page is 1-based; bbox is normalised. */
  frameRegion: { page: number; bbox: [number, number, number, number] } | null;
  pendingIds: Set<number>;
  lastError: { id: number; message: string } | null;
}

interface AnnotationStoreActions {
  hydrate: (paperId: number | null, annotations: Annotation[]) => void;
  /** Optimistically add a newly-created annotation (from AnnotationCreator). */
  addAnnotation: (annotation: Annotation) => void;
  select: (id: number | null) => void;
  selectWord: (key: string | null) => void;
  /** Replace the group-highlight set (raster criterion/task expansion). */
  setHighlight: (ids: number[] | null) => void;
  /** Set (or clear) the green task-answer frame drawn on the scan. */
  setFrameRegion: (
    region: { page: number; bbox: [number, number, number, number] } | null,
  ) => void;
  /** Accept (system suggestion) or accept-with-deduction. */
  accept: (id: number, opts?: { deduction?: number; acceptedText?: string }) => Promise<void>;
  dismiss: (id: number) => Promise<void>;
  /** Undo a dismiss — return the annotation to `pending` so it counts again
   * toward the raster load (and reappears as a card). */
  restore: (id: number) => Promise<void>;
  /** OCR-flag: confirm Azure's reading as-is. */
  confirm: (id: number) => Promise<void>;
  /** OCR-flag: replace Azure's reading. Also triggers the SC-01 word
   * recheck so any language suggestion the bad reading caused is
   * invalidated. */
  correct: (id: number, acceptedText: string) => Promise<void>;
  /** Generic partial edit — used by the card editor. */
  edit: (id: number, payload: AnnotationActionPayload) => Promise<void>;
  /** SC-10: full LanguageTool recheck against the corrected transcript. */
  rerunLanguage: (paperId: number, opts?: { invalidateDismissed?: boolean }) => Promise<void>;
  clearError: () => void;
}

export type AnnotationStore = AnnotationStoreState & AnnotationStoreActions;

/** SWR cache key for a paper's annotations. Single source of truth — used
 * by both the `useAnnotations` hook and the store's revalidation calls. */
export function annotationsKey(paperId: number): string {
  return `/api/papers/${paperId}/annotations`;
}

let currentPaperId: number | null = null;

export const useAnnotationStore = create<AnnotationStore>((set, get) => ({
  annotations: [],
  selectedId: null,
  selectedWordKey: null,
  highlightIds: new Set(),
  frameRegion: null,
  pendingIds: new Set(),
  lastError: null,

  hydrate(paperId, annotations) {
    if (paperId !== currentPaperId) {
      currentPaperId = paperId;
      set({
        annotations,
        selectedId: null,
        selectedWordKey: null,
        highlightIds: new Set(),
        frameRegion: null,
        pendingIds: new Set(),
        lastError: null,
      });
      return;
    }
    set({ annotations });
  },

  addAnnotation(annotation) {
    const current = get().annotations;
    if (current.some((a) => a.id === annotation.id)) return;
    set({ annotations: [...current, annotation] });
  },

  select(id) {
    set({ selectedId: id, selectedWordKey: null });
  },

  selectWord(key) {
    set({ selectedWordKey: key, selectedId: null });
  },

  setHighlight(ids) {
    set({ highlightIds: new Set(ids ?? []) });
  },

  setFrameRegion(region) {
    set({ frameRegion: region });
  },

  clearError() {
    set({ lastError: null });
  },

  accept(id, opts) {
    const payload: AnnotationActionPayload = { status: "accepted" };
    if (opts?.deduction != null) payload.point_deduction = opts.deduction;
    if (opts?.acceptedText != null) payload.accepted_text = opts.acceptedText;
    return runAction(id, payload, set, get);
  },

  dismiss(id) {
    return runAction(id, { status: "dismissed" }, set, get);
  },

  restore(id) {
    return runAction(id, { status: "pending" }, set, get);
  },

  confirm(id) {
    return runAction(id, { status: "confirmed" }, set, get);
  },

  async correct(id, acceptedText) {
    // SC-01: also trigger the per-word recheck so any language annotation
    // the bad reading triggered gets invalidated. Fire-and-forget — failures
    // here don't roll back the correction itself.
    const target = get().annotations.find((a) => a.id === id);
    await runAction(
      id,
      { status: "corrected", accepted_text: acceptedText },
      set,
      get,
    );
    if (target) {
      void mutate(`/api/papers/${target.paper_id}/transcript`);
      try {
        await apiFetch(`/api/papers/${target.paper_id}/recheck_word`, {
          method: "POST",
          body: JSON.stringify({
            annotation_id: id,
            page_number: target.page_number,
            x_norm: target.x_norm,
            y_norm: target.y_norm,
          }),
        });
        void mutate(annotationsKey(target.paper_id));
      } catch {
        // Silent — the user's correction is already saved; the language
        // invalidation is a courtesy.
      }
    }
  },

  edit(id, payload) {
    return runAction(id, payload, set, get);
  },

  async rerunLanguage(paperId, opts) {
    try {
      await apiFetch(`/api/papers/${paperId}/recheck_language`, {
        method: "POST",
        body: JSON.stringify({
          invalidate_dismissed: opts?.invalidateDismissed === true,
        }),
      });
      // Immediately revalidate so invalidated annotations disappear from the
      // UI. The caller is responsible for polling until new rows appear.
      await mutate(annotationsKey(paperId));
    } catch (err) {
      set({ lastError: { id: paperId, message: (err as Error).message } });
      throw err;
    }
  },
}));

async function runAction(
  id: number,
  payload: AnnotationActionPayload,
  set: (partial: Partial<AnnotationStoreState>) => void,
  get: () => AnnotationStore,
) {
  // Optimistic local update so the UI reacts before the round trip.
  const before = get().annotations;
  const optimistic = before.map((a) =>
    a.id === id ? { ...a, ...mergeOptimistic(a, payload) } : a,
  );

  set({
    annotations: optimistic,
    pendingIds: withId(get().pendingIds, id),
    lastError: null,
  });

  try {
    const updated = await apiJson<Annotation>(`/api/annotations/${id}`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
    set({
      annotations: get().annotations.map((a) => (a.id === id ? updated : a)),
      pendingIds: withoutId(get().pendingIds, id),
    });
    // Revalidate SWR so any other component reading this paper's annotation
    // list (the Konva overlay, the count badges) picks up the server's
    // canonical state — and a missed broadcast through the store is healed.
    if (currentPaperId != null) {
      void mutate(annotationsKey(currentPaperId));
    }
  } catch (err) {
    const message =
      err instanceof ApiError ? extractDetail(err.body) ?? err.message : (err as Error).message;
    // Roll back the optimistic update.
    set({
      annotations: before,
      pendingIds: withoutId(get().pendingIds, id),
      lastError: { id, message },
    });
    throw err;
  }
}

function mergeOptimistic(_a: Annotation, payload: AnnotationActionPayload): Partial<Annotation> {
  const patch: Partial<Annotation> = {};
  if (payload.status !== undefined) patch.status = payload.status;
  if (payload.accepted_text !== undefined) patch.accepted_text = payload.accepted_text;
  if (payload.point_deduction !== undefined) patch.point_deduction = payload.point_deduction;
  if (payload.content_note !== undefined) patch.content_note = payload.content_note;
  return patch;
}

function withId(set: Set<number>, id: number): Set<number> {
  const next = new Set(set);
  next.add(id);
  return next;
}

function withoutId(set: Set<number>, id: number): Set<number> {
  const next = new Set(set);
  next.delete(id);
  return next;
}

function extractDetail(body: unknown): string | null {
  if (body && typeof body === "object" && "detail" in body) {
    const detail = (body as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
  }
  return null;
}
